import { supabase } from '../lib/supabase';

class PaymentService {
  // Orders Management
  async createOrder(orderData) {
    try {
      const { data, error } = await supabase?.from('orders')?.insert(orderData)?.select()?.single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  async getOrdersByUser(userId) {
    try {
      const { data, error } = await supabase?.from('orders')?.select(`
          *,
          order_items (
            *,
            services (name, category, price),
            products (name, price)
          ),
          payments (*)
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  async getOrderById(orderId) {
    try {
      const { data, error } = await supabase?.from('orders')?.select(`
          *,
          order_items (
            *,
            services (name, category, description, price),
            products (name, description, price)
          ),
          payments (*)
        `)?.eq('id', orderId)?.single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  async updateOrder(orderId, updates) {
    try {
      const { data, error } = await supabase?.from('orders')?.update(updates)?.eq('id', orderId)?.select()?.single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  // Order Items Management
  async addOrderItem(orderItem) {
    try {
      const { data, error } = await supabase?.from('order_items')?.insert(orderItem)?.select()?.single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  async removeOrderItem(itemId) {
    try {
      const { data, error } = await supabase?.from('order_items')?.delete()?.eq('id', itemId);
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  // Services Management
  async getServices() {
    try {
      const { data, error } = await supabase?.from('services')?.select('*')?.eq('is_active', true)?.order('name');
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  async getServicesByCategory(category) {
    try {
      const { data, error } = await supabase?.from('services')?.select('*')?.eq('category', category)?.eq('is_active', true)?.order('name');
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  // Products Management
  async getProducts() {
    try {
      const { data, error } = await supabase?.from('products')?.select('*')?.eq('is_active', true)?.order('name');
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  // Payments Management
  async createPayment(paymentData) {
    try {
      const { data, error } = await supabase?.from('payments')?.insert(paymentData)?.select()?.single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  async updatePaymentStatus(paymentId, status, gatewayData = {}) {
    try {
      const updates = {
        status,
        updated_at: new Date()?.toISOString(),
        ...gatewayData
      };

      if (status === 'completed') {
        updates.processed_at = new Date()?.toISOString();
      }

      const { data, error } = await supabase?.from('payments')?.update(updates)?.eq('id', paymentId)?.select()?.single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  async getPaymentsByUser(userId) {
    try {
      const { data, error } = await supabase?.from('payments')?.select(`
          *,
          orders (
            customer_name,
            total_amount,
            service_date
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  async getPaymentById(paymentId) {
    try {
      const { data, error } = await supabase?.from('payments')?.select(`
          *,
          orders (
            *,
            order_items (
              *,
              services (name, category),
              products (name)
            )
          )
        `)?.eq('id', paymentId)?.single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  // Payment Gateway Mock Integrations
  async processPayFastPayment(paymentData) {
    try {
      // Mock PayFast integration - replace with actual API calls
      const mockResponse = {
        success: true,
        transaction_id: `pf_${Date.now()}`,
        reference: `PF_${Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase()}`,
        status: 'completed',
        amount: paymentData?.amount,
        currency: 'ZAR'
      };

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      return { data: mockResponse, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  async processPaystackPayment(paymentData) {
    try {
      // Mock Paystack integration - replace with actual API calls
      const mockResponse = {
        success: true,
        transaction_id: `ps_${Date.now()}`,
        reference: `PS_${Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase()}`,
        status: 'completed',
        amount: paymentData?.amount,
        currency: 'ZAR'
      };

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      return { data: mockResponse, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  // Real-time subscription for payment status updates
  subscribeToPaymentUpdates(paymentId, callback) {
    return supabase?.channel('payment_updates')?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'payments',
          filter: `id=eq.${paymentId}`
        },
        callback
      )?.subscribe();
  }

  // Utility functions
  calculateOrderTotal(orderItems) {
    return orderItems?.reduce((total, item) => {
      return total + (item?.total_price || 0);
    }, 0) || 0;
  }

  formatCurrency(amount, currency = 'ZAR') {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: currency
    })?.format(amount);
  }

  getPaymentStatusColor(status) {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      refunded: 'bg-purple-100 text-purple-800'
    };
    return statusColors?.[status] || statusColors?.pending;
  }

  getOrderStatusColor(status) {
    const statusColors = {
      draft: 'bg-gray-100 text-gray-800',
      confirmed: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return statusColors?.[status] || statusColors?.draft;
  }
}

export const paymentService = new PaymentService();